import { Icon, MenuItem, Tooltip } from "@material-ui/core";
import React, { Fragment, useEffect, useState } from "react";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IWorkspacePermissionAccess,
    IWorkspaceResource,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePermissionSubjectType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole,
    WorkspacePlanTier,
} from "common/interfaces";
import {
    PresentationLibraryFilter,
    PresentationLibraryFilterType,
} from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController, {
    getWorkspaceResources,
    getUserPermissionsToResourceFolder,
    WorkspaceControllerState,
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowInputDialog,
    ShowWarningDialog,
} from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import { IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { moveSubfolderContentsToParentFolder } from "js/react/views/SharedResources/utils/moveSubfolderContentsToParentFolder";
import { ManageTeamFolderMembersDialog } from "js/react/views/TeamResources/dialogs/TeamFolderDialogs";
import { t } from "js/i18n";

const logger = getLogger();

const HeaderActionIconMenu = styled(IconMenu).attrs({
    size: "small",
    fontSize: "small",
})`
    &&& {
        padding: 0px;
    }
`;

const RestrictedMenuItem = styled(MenuItem)`
    &&& {
        color: ${themeColors.lighterGray};
    }
`;

export async function getPresentationResourcesInFolder(folderId: string): Promise<IWorkspaceResource[]> {
    return getWorkspaceResources({
        workspaceId: WorkspaceController.workspaceId,
        resourceTypes: [WorkspaceResourceType.PRESENTATION],
        resourceFilters: [{ field: "folderIds", operator: "eq", value: folderId }],
    });
}

export function isPresentationFolderNameTaken(
    name: string,
    folders: IWorkspaceResourceFolder[],
    {
        parentFolderId,
        folderTypes,
        excludeFolderId,
    }: {
        parentFolderId?: string | null;
        folderTypes?: WorkspaceResourceFolderType[];
        excludeFolderId?: string;
    }
) {
    return folders
        .filter(f => {
            if (f.resourcesType !== WorkspaceResourceType.PRESENTATION) {
                return false;
            }
            if (excludeFolderId && f.id === excludeFolderId) {
                return false;
            }
            if (folderTypes && !folderTypes.includes(f.folderType)) {
                return false;
            }
            if (parentFolderId) {
                return f.parentFolderId === parentFolderId;
            }
            return !f.parentFolderId;
        })
        .some(f => f.name === name);
}

interface FolderMenuItemsProps {
    folder: IWorkspaceResourceFolder;
    resourceFolders: IWorkspaceResourceFolder[];
    onSetFilter: (filter: PresentationLibraryFilter) => void;
}

function handleProUpgradeAction(cta: string) {
    if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
        ShowDialog(GuestWorkspaceDialog);
    } else {
        ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.PRO, analytics: { cta } });
    }
}

function handleTeamUpgradeAction() {
    if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
        ShowDialog(GuestWorkspaceDialog);
    } else {
        ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.TEAM });
    }
}

export function UserFolderMenuItems({ folder, resourceFolders, onSetFilter }: FolderMenuItemsProps) {
    const canRenameUserFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToRenameUserFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.RESOURCE_FOLDERS,
        PermissionActionType.CREATE
    );
    const canDeleteUserFolders = canRenameUserFolders;
    const canUpgradeToDeleteUserFolders = canUpgradeToRenameUserFolders;

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Folder"),
            maxChars: 50,
            value: folder.name,
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, {
                parentFolderId: null,
                folderTypes: [WorkspaceResourceFolderType.PERSONAL],
                excludeFolderId: folder.id,
            })) {
                throw new Error(t("Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t("Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name }),
            });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][UserFolder] handleRenameFolder() Unable to rename folder");

            ShowErrorDialog({
                title: t("Unable to rename folder"),
                message: err.message,
            });
        }
    };

    const handleDeleteFolder = async () => {
        try {
            const resourcesInFolder = await getPresentationResourcesInFolder(folder.id);
            if (resourcesInFolder.length > 0) {
                throw new Error(t("This folder contains presentations and can't be deleted."));
            }

            await withProgressDialog({
                title: t("Deleting folder..."),
                action: () => WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id }),
            });

            onSetFilter({ type: PresentationLibraryFilterType.ALL_PRESENTATIONS });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][UserFolder] handleDeleteFolder() Unable to delete folder");

            ShowErrorDialog({
                title: t("Unable to delete folder"),
                message: err.message,
            });
        }
    };

    return (
        <>
            {canRenameUserFolders &&
                <MenuItem onClick={handleRenameFolder}>
                    <Icon>label</Icon>
                    {t("Rename Folder")}
                </MenuItem>}
            {canUpgradeToRenameUserFolders && (
                <RestrictedMenuItem divider onClick={() => handleProUpgradeAction("rename_folder")}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    {t("Rename Folder")}
                </RestrictedMenuItem>
            )}
            <hr />
            {canDeleteUserFolders &&
                <MenuItem onClick={handleDeleteFolder}>
                    <Icon>delete</Icon>
                    {t("Delete Folder")}
                </MenuItem>}
            {canUpgradeToDeleteUserFolders &&
                <RestrictedMenuItem onClick={() => handleProUpgradeAction("delete_folder")}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    {t("Delete Folder")}
                </RestrictedMenuItem>}
        </>
    );
}

interface TeamFolderMenuItemsProps extends FolderMenuItemsProps {
    permissions: IWorkspacePermissionAccess;
}

export function TeamFolderMenuItems({ folder, resourceFolders, onSetFilter, permissions }: TeamFolderMenuItemsProps) {
    const isDefault = folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;
    const subFolders = resourceFolders
        .filter(f => f.resourcesType === WorkspaceResourceType.PRESENTATION && f.folderType === WorkspaceResourceFolderType.SHARED && f.parentFolderId === folder.id)
        .sort((a, b) => a.name.localeCompare(b.name));
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );
    const canUpgradeToRenameTeamFolder = canUpgradeToManageSharedResources;
    const canRenameTeamFolder = !canUpgradeToRenameTeamFolder && permissions.write.object && !isDefault;
    const canUpgradeToCreateSubfolder = canUpgradeToManageSharedResources;
    const canCreateSubfolder = !canUpgradeToCreateSubfolder && permissions.write.object;

    const handleEditSharing = () => {
        if (isDefault) {
            ShowWarningDialog({
                title: t("Sharing can't be modified"),
                message: t("The All Team Members folder is shared with your entire team and can't be modified or deleted."),
            });
            return;
        }

        ShowDialog(ManageTeamFolderMembersDialog, {
            folderId: folder.id,
        });
    };

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Folder"),
            maxChars: 50,
            value: folder.name,
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, {
                parentFolderId: null,
                folderTypes: [WorkspaceResourceFolderType.SHARED, WorkspaceResourceFolderType.SHARED_DEFAULT],
                excludeFolderId: folder.id,
            })) {
                throw new Error(t("Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t("Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name }),
            });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][TeamFolder] handleRenameFolder() Unable to rename folder");

            ShowErrorDialog({
                title: t("Unable to rename folder"),
                message: err.message,
            });
        }
    };

    const handleCreateSubFolder = async () => {
        let name = await ShowInputDialog({
            title: t("New folder"),
            maxChars: 100,
            value: t("New Folder"),
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (!name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, { parentFolderId: folder.id })) {
                throw new Error(t("Please enter a unique subfolder name."));
            }

            const createdFolder = await withProgressDialog({
                title: t("Creating subfolder..."),
                action: () => WorkspaceController.createWorkspaceResourceFolder({
                    parentFolderId: folder.id,
                    name,
                    resourcesType: WorkspaceResourceType.PRESENTATION,
                    folderType: WorkspaceResourceFolderType.SHARED,
                }),
            });

            // After creating the subfolder, set the filter to the new subfolder
            onSetFilter({
                type: PresentationLibraryFilterType.TEAM_FOLDER,
                folderId: folder.id,
                subFolderId: createdFolder.id,
            });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][TeamFolder] handleCreateSubFolder() Unable to create subfolder");

            ShowErrorDialog({
                title: t("Unable to create subfolder"),
                message: err.message,
            });
        }
    };

    const handleDeleteFolder = async () => {
        if (await ShowConfirmationDialog({
            title: t("Delete this shared team folder?"),
            message: <Fragment>
                <p>{t("Presentations in the folder will NOT be deleted, but members may lose access.")}</p>
                <br />
                <p>{t("You cannot undo this action.")}</p>
            </Fragment>,
        })) {
            try {
                await withProgressDialog({
                    title: t("Deleting folder..."),
                    action: async () => {
                        for (const subFolder of subFolders) {
                            await WorkspaceController.deleteWorkspaceResourceFolder({ folderId: subFolder.id });
                        }

                        await WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id });
                    },
                });

                onSetFilter({ type: PresentationLibraryFilterType.ALL_PRESENTATIONS });
            } catch (err) {
                if (err) {
                    logger.error(err, "[PresentationLibraryFolderMenu][TeamFolder] handleDeleteFolder() failed", { folderId: folder.id });

                    ShowErrorDialog({
                        error: t("Failed to delete team folder"),
                        message: t("Something went wrong. Try again or contact support@beautiful.ai if the issue persists."),
                    });
                }
            }
        }
    };

    const handleLeaveFolder = async () => {
        if (await ShowConfirmationDialog({
            title: t("Leave this team folder?"),
            message: t("You may lose access to presentations in this folder. You can rejoin only if a team owner or librarian invites you."),
        })) {
            onSetFilter({ type: PresentationLibraryFilterType.ALL_PRESENTATIONS });

            try {
                await withProgressDialog({
                    title: t("Leaving team folder..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsers({
                        folderId: folder.id,
                        update: { remove: [{ subjectId: app.user.id, subjectType: WorkspacePermissionSubjectType.USER }] },
                    }),
                });
            } catch (err) {
                logger.error(err, "[PresentationLibraryFolderMenu][TeamFolder] handleLeaveFolder() Unable to leave team folder");

                ShowErrorDialog({
                    title: t("Unable to leave team folder"),
                    message: err.message,
                });
            }
        }
    };

    return (
        <>
            {!isDefault && !canUpgradeToManageSharedResources && (
                <MenuItem divider onClick={handleEditSharing}>
                    <Icon>people_add</Icon>
                    {permissions.write.permissions ? t("Share Team Folder...") : t("View Sharing...")}
                </MenuItem>
            )}
            {canUpgradeToManageSharedResources && (
                <RestrictedMenuItem divider onClick={handleTeamUpgradeAction}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    {t("Share Team Folder...")}
                </RestrictedMenuItem>
            )}
            {canRenameTeamFolder && (
                <MenuItem divider onClick={handleRenameFolder}>
                    <Icon>label</Icon>
                    {t("Rename Team Folder...")}
                </MenuItem>
            )}
            {canUpgradeToRenameTeamFolder && (
                <RestrictedMenuItem divider onClick={handleTeamUpgradeAction}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    {t("Rename Team Folder...")}
                </RestrictedMenuItem>
            )}
            {canCreateSubfolder && (
                <MenuItem divider onClick={handleCreateSubFolder}>
                    <Icon>folder_open</Icon>
                    {t("Create SubFolder...")}
                </MenuItem>
            )}
            {canUpgradeToCreateSubfolder && (
                <RestrictedMenuItem divider onClick={handleTeamUpgradeAction}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    {t("Create SubFolder...")}
                </RestrictedMenuItem>
            )}
            {permissions.owner && !isDefault && !canUpgradeToManageSharedResources && (
                <MenuItem onClick={handleDeleteFolder}>
                    <Icon>delete</Icon>
                    {t("Delete Team Folder")}
                </MenuItem>
            )}
            {permissions.owner && !isDefault && canUpgradeToManageSharedResources && (
                <RestrictedMenuItem onClick={handleTeamUpgradeAction}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    {t("Delete Team Folder")}
                </RestrictedMenuItem>
            )}
            {!permissions.owner && !isDefault && (
                <MenuItem onClick={handleLeaveFolder}>
                    <Icon>logout</Icon>
                    {t("Leave Team Folder")}
                </MenuItem>
            )}
        </>
    );
}

export function TeamSubFolderMenuItems({ folder, resourceFolders, onSetFilter, permissions }: TeamFolderMenuItemsProps) {
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Subfolder"),
            maxChars: 50,
            value: folder.name,
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, {
                parentFolderId: folder.parentFolderId,
                excludeFolderId: folder.id,
            })) {
                throw new Error(t("Please enter a unique subfolder name."));
            }

            await withProgressDialog({
                title: t("Renaming subfolder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name }),
            });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][TeamSubFolder] handleRenameFolder() Unable to rename subfolder");

            ShowErrorDialog({
                title: t("Unable to rename subfolder"),
                message: err.message,
            });
        }
    };

    const handleDeleteFolder = async () => {
        try {
            const resourcesInFolder = await getPresentationResourcesInFolder(folder.id);
            if (resourcesInFolder.length > 0) {
                if (!await ShowConfirmationDialog({
                    title: t("Delete this subfolder?"),
                    message: <p>{t("Presentations will move back to the root team folder and will not be deleted.")}</p>,
                })) {
                    return;
                }
            }

            await withProgressDialog({
                title: t("Deleting subfolder..."),
                action: async () => {
                    await moveSubfolderContentsToParentFolder(
                        folder,
                        [WorkspaceResourceType.PRESENTATION],
                        {
                            read: { object: true, permissions: true },
                            write: { object: true, permissions: true },
                        }
                    );
                    await WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id });
                },
            });

            onSetFilter({ type: PresentationLibraryFilterType.TEAM_FOLDER, folderId: folder.parentFolderId });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][TeamSubFolder] handleDeleteFolder() Unable to delete subfolder");

            ShowErrorDialog({
                title: t("Unable to delete subfolder"),
                message: err.message,
            });
        }
    };

    if (permissions.write.object && !canUpgradeToManageSharedResources) {
        return (
            <>
                <MenuItem onClick={handleRenameFolder}>
                    <Icon>label</Icon>
                    {t("Rename Folder")}
                </MenuItem>
                <hr />
                <MenuItem onClick={handleDeleteFolder}>
                    <Icon>delete</Icon>
                    {t("Delete Folder")}
                </MenuItem>
            </>
        );
    }

    if (canUpgradeToManageSharedResources) {
        return (
            <>
                <RestrictedMenuItem onClick={handleTeamUpgradeAction}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    {t("Rename Folder")}
                </RestrictedMenuItem>
                <hr />
                <RestrictedMenuItem onClick={handleTeamUpgradeAction}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    {t("Delete Folder")}
                </RestrictedMenuItem>
            </>
        );
    }

    return null;
}

interface PresentationLibraryFolderHeaderMenuProps {
    filter: PresentationLibraryFilter;
    readOnly?: boolean;
    onSetFilter: (filter: PresentationLibraryFilter) => void;
}

function PresentationLibraryFolderHeaderMenuInner({
    filter,
    readOnly,
    onSetFilter,
    resourceFolders,
    workspace: { id: workspaceId },
}: PresentationLibraryFolderHeaderMenuProps & WorkspaceControllerState) {
    const [permissions, setPermissions] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());

    const folderId = filter.subFolderId || filter.folderId;
    const folder = resourceFolders.find(f => f.id === folderId);
    const isTeamFolder = filter.type === PresentationLibraryFilterType.TEAM_FOLDER;
    const isSubFolder = !!filter.subFolderId;
    const isUserFolder = filter.type === PresentationLibraryFilterType.FOLDER;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );

    useEffect(() => {
        if (!isTeamFolder || !filter.folderId) {
            return;
        }

        (async () => {
            try {
                const fetchedPermissions = await getUserPermissionsToResourceFolder({
                    workspaceId,
                    folderId: filter.folderId,
                });
                setPermissions(fetchedPermissions);
            } catch (err) {
                logger.error(err, "[PresentationLibraryFolderMenu] useEffect() Unable to fetch permissions");
            }
        })();
    }, [filter.folderId, isTeamFolder, workspaceId]);

    if (!folder || readOnly || app.isConstrained) {
        return null;
    }

    let menuItems: React.ReactNode = null;

    if (isUserFolder) {
        menuItems = (
            <UserFolderMenuItems
                folder={folder}
                resourceFolders={resourceFolders}
                onSetFilter={onSetFilter}
            />
        );
    } else if (isTeamFolder && isSubFolder) {
        if (!permissions.write.object && !canUpgradeToManageSharedResources) {
            return null;
        }

        menuItems = (
            <TeamSubFolderMenuItems
                folder={folder}
                resourceFolders={resourceFolders}
                onSetFilter={onSetFilter}
                permissions={permissions}
            />
        );
    } else if (isTeamFolder) {
        menuItems = (
            <TeamFolderMenuItems
                folder={folder}
                resourceFolders={resourceFolders}
                onSetFilter={onSetFilter}
                permissions={permissions}
            />
        );
    }

    if (!menuItems) {
        return null;
    }

    return (
        <Tooltip title={t("Manage folder")}>
            <HeaderActionIconMenu>
                {menuItems}
            </HeaderActionIconMenu>
        </Tooltip>
    );
}

export const PresentationLibraryFolderHeaderMenu = WorkspaceController.withInitializedState(
    PresentationLibraryFolderHeaderMenuInner
);
